import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import type { PublicKey, Transaction, VersionedTransaction } from '@solana/web3.js';
import { PublicKey as SolPublicKey } from '@solana/web3.js';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, ArrowUpFromLine, AlertTriangle, ExternalLink, Check } from 'lucide-react';
import { useWithdraw } from '@/hooks/useWithdraw';
import { useWithdrawalAddresses } from '@/hooks/useWithdrawalAddresses';
import { WithdrawAddressDropdown } from './WithdrawAddressDropdown';
import type { WalletToken } from '@/hooks/useTokenBalances';

const MAX_RETRIES = 3;

interface WithdrawTabProps {
  publicKey: PublicKey | null;
  sendTransaction: ((tx: Transaction | VersionedTransaction, connection?: unknown) => Promise<string>) | undefined;
  balance: number | null;
  selectedToken: WalletToken | null;
  refetchBalance: () => void;
  refetchTokens: () => void;
  onPreSign?: () => Promise<void>;
}

function isValidSolanaAddress(address: string): boolean {
  try {
    new SolPublicKey(address);
    return true;
  } catch {
    return false;
  }
}

function getSolscanUrl(signature: string): string {
  const isDevnet = import.meta.env.VITE_USE_DEVNET === 'true';
  const cluster = isDevnet ? '?cluster=devnet' : '';
  return `https://solscan.io/tx/${signature}${cluster}`;
}

export const WithdrawTab = ({
  publicKey,
  sendTransaction,
  balance,
  selectedToken,
  refetchBalance,
  refetchTokens,
  onPreSign,
}: WithdrawTabProps) => {
  const walletAddress = publicKey?.toBase58();
  const { addresses, recordAddress, updateLabel, clearHistory } = useWithdrawalAddresses(walletAddress);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [pendingLabel, setPendingLabel] = useState('');
  const addressContainerRef = useRef<HTMLDivElement>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const {
    withdrawAddress,
    setWithdrawAddress,
    withdrawAmount,
    setWithdrawAmount,
    withdrawStatus,
    pendingSignature,
    needsAtaCreation,
    retryCount,
    handleMaxAmount,
    handleWithdraw,
    isWithdrawing,
    withdrawSymbol,
  } = useWithdraw({
    publicKey,
    sendTransaction,
    balance,
    selectedToken,
    refetchBalance,
    refetchTokens,
    onPreSign,
    onWithdrawConfirmed: (address: string) => {
      recordAddress.mutate({ address });
    },
  });

  // Address validation (instant, no async)
  const isAddressValid = useMemo(
    () => withdrawAddress.length > 0 && isValidSolanaAddress(withdrawAddress),
    [withdrawAddress]
  );
  const showInvalidError = withdrawAddress.length > 0 && !isAddressValid;

  // Check if address is already saved
  const existingSavedAddress = useMemo(
    () => addresses.find(a => a.address === withdrawAddress),
    [addresses, withdrawAddress]
  );
  const isNewValidAddress = isAddressValid && !existingSavedAddress;

  // Reset pending label when address changes
  useEffect(() => {
    setPendingLabel('');
  }, [withdrawAddress]);

  // Close dropdown on click outside
  useEffect(() => {
    if (!isDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (addressContainerRef.current && !addressContainerRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isDropdownOpen]);

  // Clean up blur timeout on unmount
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    };
  }, []);

  const handleAddressFocus = useCallback(() => {
    if (addresses.length > 0) {
      setIsDropdownOpen(true);
    }
  }, [addresses.length]);

  const handleAddressBlur = useCallback((e: React.FocusEvent) => {
    // If focus is moving to another element inside the dropdown container, don't close
    if (addressContainerRef.current?.contains(e.relatedTarget as Node)) {
      return;
    }
    // Delay close to allow click on dropdown items to register
    blurTimeoutRef.current = setTimeout(() => {
      setIsDropdownOpen(false);
    }, 150);
  }, []);

  const handleSelectAddress = useCallback((address: string) => {
    setWithdrawAddress(address);
    setIsDropdownOpen(false);
  }, [setWithdrawAddress]);

  const handleSaveLabel = useCallback(() => {
    if (!withdrawAddress || !isAddressValid) return;
    const sanitized = pendingLabel.replace(/[^a-zA-Z0-9 \-.]/g, '').trim();
    recordAddress.mutate({
      address: withdrawAddress,
      label: sanitized || undefined,
    });
    setPendingLabel('');
  }, [withdrawAddress, isAddressValid, pendingLabel, recordAddress]);

  return (
    <div className="flex flex-col space-y-4">
      <div className="space-y-2">
        <Label>Destination Address</Label>
        <div className="relative" ref={addressContainerRef}>
          <Input
            key={showInvalidError ? withdrawAddress : undefined}
            placeholder="Enter Solana address"
            value={withdrawAddress}
            onChange={(e) => setWithdrawAddress(e.target.value)}
            onFocus={handleAddressFocus}
            onBlur={handleAddressBlur}
            className={`font-mono text-sm ${isAddressValid ? 'pr-10' : ''} ${showInvalidError ? 'border-destructive animate-[shake_0.3s_ease-in-out]' : ''}`}
          />
          {isAddressValid && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <Check className="h-4 w-4 text-green-400" />
            </span>
          )}
          {showInvalidError && (
            <p className="text-xs text-destructive mt-1">Invalid Solana address</p>
          )}
          <WithdrawAddressDropdown
            isOpen={isDropdownOpen}
            addresses={addresses}
            filterText={withdrawAddress}
            onSelect={handleSelectAddress}
            onUpdateLabel={(id, label) => updateLabel.mutate({ id, label })}
            onClearHistory={() => clearHistory.mutate()}
          />
        </div>
        {isNewValidAddress && (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={pendingLabel}
              onChange={(e) => setPendingLabel(e.target.value)}
              maxLength={20}
              placeholder="Label this address (optional)"
              className="flex-1 min-w-0 bg-background/50 border border-border rounded px-2 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <Button variant="outline" size="sm" onClick={handleSaveLabel}>
              Save
            </Button>
          </div>
        )}
        {existingSavedAddress?.label && (
          <p className="text-xs text-muted-foreground">
            Saved as: {existingSavedAddress.label}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex justify-between">
          <Label>Amount ({withdrawSymbol})</Label>
          <button
            className="text-xs text-primary hover:underline"
            onClick={handleMaxAmount}
          >
            Max
          </button>
        </div>
        <Input
          type="number"
          step="0.000001"
          min="0"
          placeholder="0.00"
          value={withdrawAmount}
          onChange={(e) => setWithdrawAmount(e.target.value)}
        />
      </div>

      <div className="space-y-1">
        <p className="text-xs text-muted-foreground">
          Network fee: ~0.000005 SOL
        </p>
        {selectedToken !== null && needsAtaCreation && (
          <p className="text-xs text-muted-foreground">
            New account fee: ~0.002 SOL (one-time)
          </p>
        )}
        {selectedToken !== null && balance !== null && balance < 0.005 && (
          <p className="text-xs text-destructive font-medium">
            Insufficient SOL for transaction fees. You need at least 0.005 SOL.
          </p>
        )}
      </div>

      <Button
        className="w-full"
        onClick={handleWithdraw}
        disabled={isWithdrawing || !withdrawAddress || !isAddressValid || !withdrawAmount || (selectedToken !== null && balance !== null && balance < 0.005)}
      >
        {withdrawStatus === 'signing' && (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            {retryCount > 0 ? `Retry ${retryCount}/${MAX_RETRIES}...` : 'Signing...'}
          </>
        )}
        {withdrawStatus === 'broadcasting' && (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Broadcasting...
          </>
        )}
        {withdrawStatus === 'confirming' && (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Confirming...
          </>
        )}
        {withdrawStatus === 'idle' && (
          <>
            <ArrowUpFromLine className="h-4 w-4 mr-2" />
            Withdraw {withdrawSymbol}
          </>
        )}
        {withdrawStatus === 'success' && (
          <>
            <Check className="h-4 w-4 mr-2" />
            Success!
          </>
        )}
        {withdrawStatus === 'error' && (
          <>
            <AlertTriangle className="h-4 w-4 mr-2" />
            Failed
          </>
        )}
        {withdrawStatus === 'pending' && (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Pending...
          </>
        )}
      </Button>

      {/* Show pending transaction link */}
      {withdrawStatus === 'pending' && pendingSignature && (
        <div className="rounded-lg border border-warning/30 bg-warning/5 p-3 text-center">
          <p className="text-sm text-muted-foreground mb-2">
            Transaction submitted but confirmation is taking longer than expected.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(getSolscanUrl(pendingSignature), '_blank')}
          >
            <ExternalLink className="h-3 w-3 mr-1" />
            Check on Solscan
          </Button>
        </div>
      )}
    </div>
  );
};
